// One PDF page: a canvas (sharp on Retina) plus pdf.js' text layer, so text can be selected and copied.
// Rendered only when the page comes near the visible area; a zoom change re-renders after a short pause while the
// old bitmap is stretched meanwhile.
import { useEffect, useRef, useState } from 'react';
import type { PDFDocumentProxy, RenderTask } from 'pdfjs-dist';
import { loadPdfjs } from './pdfjs';

export interface PageSize { w: number; h: number } // in PDF points at scale 1, rotation applied

interface Props {
  doc: PDFDocumentProxy; n: number; size: PageSize; scale: number; rotation: number;
  text?: boolean; highlight?: string; root?: Element | null; onShow?: (n: number) => void;
}

export function PdfPage({ doc, n, size, scale, rotation, text = true, highlight, root }: Props) {
  const box = useRef<HTMLDivElement>(null), canvasRef = useRef<HTMLCanvasElement>(null), textRef = useRef<HTMLDivElement>(null);
  // The first pages render right away: they are visible anyway, and they must not depend on the IntersectionObserver
  // firing (it may not in a hidden window or some WebViews — the preview stayed a blank white page then).
  const [near, setNear] = useState(n <= 2);
  const w = Math.round(size.w * scale), h = Math.round(size.h * scale);

  useEffect(() => {
    const el = box.current; if (!el) return;
    const io = new IntersectionObserver(([e]) => setNear(n <= 2 || !!e?.isIntersecting), { root: root ?? null, rootMargin: '800px 0px' });
    io.observe(el);
    return () => io.disconnect();
  }, [root, n]);

  useEffect(() => {
    if (!near) return;
    // An old text layer would sit at the previous zoom until the re-render: drop it right away.
    textRef.current?.replaceChildren();
    let cancelled = false, task: RenderTask | null = null, tl: { cancel(): void } | null = null;
    const t = setTimeout(async () => {
      const page = await doc.getPage(n);
      if (cancelled) return;
      const dpr = Math.min(window.devicePixelRatio || 1, 3);
      const vp = page.getViewport({ scale: scale * dpr, rotation });
      const canvas = document.createElement('canvas');
      canvas.width = Math.floor(vp.width); canvas.height = Math.floor(vp.height);
      task = page.render({ canvasContext: canvas.getContext('2d')!, viewport: vp });
      try { await task.promise; } catch { return; }
      if (cancelled || !canvasRef.current) return;
      const target = canvasRef.current;
      target.width = canvas.width; target.height = canvas.height;
      target.getContext('2d')!.drawImage(canvas, 0, 0);
      if (text && textRef.current) {
        const pdfjs = await loadPdfjs(), layer = textRef.current;
        layer.replaceChildren();
        layer.style.setProperty('--scale-factor', String(scale));
        const textLayer = new pdfjs.TextLayer({ textContentSource: page.streamTextContent(), container: layer, viewport: page.getViewport({ scale, rotation }) });
        tl = textLayer;
        await textLayer.render().catch(() => {});
        if (!cancelled && highlight) markText(layer, highlight);
      }
    }, 120);
    return () => { cancelled = true; clearTimeout(t); task?.cancel(); tl?.cancel(); };
  }, [near, doc, n, scale, rotation, text, highlight]);

  return (
    <div ref={box} data-page={n} style={{ position: 'relative', width: w, height: h, flex: 'none', background: '#fff', boxShadow: '0 6px 24px rgba(0,0,0,.35)', borderRadius: 2, overflow: 'hidden' }}>
      <canvas ref={canvasRef} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', display: 'block' }} />
      {text && <div ref={textRef} className="rc-textLayer" />}
    </div>
  );
}

/** Marks text-layer spans that contain the search term. */
function markText(layer: HTMLElement, term: string) {
  const t = term.toLowerCase();
  layer.querySelectorAll('span').forEach((s) => { if (s.textContent && s.textContent.toLowerCase().includes(t)) s.classList.add('rc-hit'); });
}
